import { useTogglePresentationMode } from '@euler/app/components/PresentationModeContext';
import { StackLayout } from '@euler/app/components/StackLayout';
import {
  LogoutButton,
  SwitchAppButton,
  SwitchMode,
  UserInfoView,
  VersionInfo,
} from '@euler/app/components/drawer/parts';
import { LogoutIcon } from '@euler/app/components/icons/LogoutIcon';
import { SwitchIcon } from '@euler/app/components/icons/SwitchIcon';
import {
  MessageIcon,
  MessageIconFill,
} from '@euler/app/components/sidebar/icons/MessageIcon';
import { kAiEditionTabs } from '@euler/app/flows/ai-edition/aiEditionTabs';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useLogout } from '@euler/app/flows/auth/hooks';
import { useChatUnreadCount } from '@euler/app/flows/chat/lib/session/hooks/useChatUnreadCount';
import { useFeedUnreadCount } from '@euler/app/flows/feed/lib/unread/hooks/useFeedUnreadCount';
import {
  PlansTabIcon,
  PlansTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/plans-icon';
import {
  ProfileTabIcon,
  ProfileTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/profile-icon';
import {
  RecordsTabIcon,
  RecordsTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/records-icon';
import {
  TasksTabIcon,
  TasksTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/tasks-icon';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { Avatar } from '@euler/components/Avatar';
import { Icon, IconType } from '@euler/components/Icomoon';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { FontFamily } from '@euler/components/typography';
import { config } from '@euler/config';
import { parseApdmCouponQr } from '@euler/functions/apdm/couponRedemption';
import { useQrcodeAuth } from '@euler/functions/auth/useQrcodeAuth';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isMobileBrowser,
  useIsDesktopLayout,
} from '@euler/functions/responsiveness';
import { useCanSeeServiceRecords } from '@euler/functions/user/useCanSeeServiceRecords';
import { StoreFeature } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { delayed, onErrorIgnore, withConfirmation } from '@euler/utils';
import { DrawerContentComponentProps } from '@react-navigation/drawer';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { Monitor, Sparkles } from 'lucide-react-native';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SidebarCreditBalance } from './SidebarCreditBalance';
import { SidebarItemBadge } from './SidebarItemBadge';
import { useEntitledSidebarItems } from './useEntitledSidebarItems';

type IconRenderer = (
  size: number,
  color: string,
  active: boolean,
) => JSX.Element;

const iconmoon =
  (active: IconType, inactive: IconType): IconRenderer =>
  (size, color, isActive) => (
    <Icon name={isActive ? active : inactive} size={size} color={color} />
  );

export type SidebarItem = {
  key: string;
  label: string;
  renderIcon: IconRenderer;
  /** Navigation target. For tab items the value matches a tab route name; for
   * shortcuts (Help) it can be a tab name with nested params. */
  target: { name: string; params?: any };
  /** Show unread badge (Inbox). */
  showUnread?: boolean;
  /** 知车AI: its trial pill, and the AI credits left under its label, show
   * while the store is trying AI (`useAiTrialEndsAt`), not its own trial. */
  aiTrial?: boolean;
  /** Kept in the list but not rendered — flip to re-surface the entry. */
  hidden?: boolean;
  /** Shown only when the store has it. */
  feature?: StoreFeature;
  /** Set by `useEntitledSidebarItems` when a trial is all that gives it. */
  trialEndsAt?: string | null;
};

const aiHomeItem: SidebarItem = {
  key: 'AiHome',
  label: '知车AI',
  target: { name: 'AiHome' },
  feature: StoreFeature.AiAssistant,
  aiTrial: true,
  renderIcon: (size, color) => <Sparkles size={size} color={color} />,
};

const dashboardTabItem: SidebarItem = {
  key: 'Dashboard',
  label: '门店看板',
  renderIcon: (size, color, active) =>
    active ? (
      <TasksTabIconFill size={size} fill={color} />
    ) : (
      <TasksTabIcon size={size} fill={color} />
    ),
  target: { name: 'Dashboard' },
  feature: StoreFeature.OrderReception,
};

const historyTabItem: SidebarItem = {
  key: 'History',
  label: '历史看板',
  renderIcon: (size, color, active) =>
    active ? (
      <RecordsTabIconFill size={size} fill={color} />
    ) : (
      <RecordsTabIcon size={size} fill={color} />
    ),
  target: { name: 'History' },
  feature: StoreFeature.OrderReception,
};

// Desktop web only: in the desktop sidebar's lists, not the phone drawer's.
const workshopBoardItem: SidebarItem = {
  key: 'WorkshopBoard',
  label: '车间看板',
  renderIcon: (size, color) => <Monitor size={size} color={color} />,
  target: { name: 'WorkshopBoard' },
  feature: StoreFeature.OrderReception,
};

const plansTabItem: SidebarItem = {
  key: 'Plans',
  label: '维保计划',
  renderIcon: (size, color, active) =>
    active ? (
      <PlansTabIconFill size={size} fill={color} />
    ) : (
      <PlansTabIcon size={size} fill={color} />
    ),
  target: { name: 'Plans' },
  feature: StoreFeature.MaintenancePlan,
};

const leadsTabItem: SidebarItem = {
  key: 'Leads',
  label: '商机管理',
  renderIcon: iconmoon('clipboard', 'clipboard'),
  target: { name: 'Leads' },
  feature: StoreFeature.Lead,
};

const reservationsTabItem: SidebarItem = {
  key: 'Reservations',
  label: '预约管理',
  renderIcon: iconmoon('clock', 'clock'),
  target: { name: 'Reservations' },
  feature: StoreFeature.Reservation,
};

const TAB_ITEMS: SidebarItem[] = [
  aiHomeItem,
  dashboardTabItem,
  historyTabItem,
  workshopBoardItem,
  plansTabItem,
  leadsTabItem,
  reservationsTabItem,
  {
    key: 'Inbox',
    label: '消息中心',
    renderIcon: (size, color, active) =>
      active ? (
        <MessageIconFill size={size} fill={color} />
      ) : (
        <MessageIcon size={size} fill={color} />
      ),
    target: { name: 'Inbox', params: { screen: '_index' } },
    showUnread: true,
  },
];

// The AI edition's tabs, in its order.
const AI_EDITION_TAB_ITEMS: SidebarItem[] = kAiEditionTabs.map(tab => ({
  key: tab.name,
  label: tab.label,
  target: { name: tab.name },
  feature: tab.feature ?? undefined,
  showUnread: tab.showUnread,
  renderIcon: tab.renderIcon,
}));

// The AI edition's tabs, then the workshop tools the store has (on trial or
// not), named as in its drawer. A restricted role has no History tab.
const buildAiEditionSidebarItems = (canSeeRecords: boolean): SidebarItem[] => [
  ...AI_EDITION_TAB_ITEMS,
  { ...dashboardTabItem, label: '在厂车辆' },
  ...(canSeeRecords ? [{ ...historyTabItem, label: '历史工单' }] : []),
  workshopBoardItem,
  plansTabItem,
  leadsTabItem,
  reservationsTabItem,
];

// Dev builds only, as the route itself is (`DevScreens`).
const playgroundItem: SidebarItem = {
  key: 'Playground',
  label: 'Playground',
  renderIcon: iconmoon('toys', 'toys'),
  target: { name: 'Playground' },
};

const SECONDARY_ITEMS: SidebarItem[] = [
  {
    key: 'Me',
    label: '我的',
    renderIcon: (size, color, active) =>
      active ? (
        <ProfileTabIconFill size={size} fill={color} />
      ) : (
        <ProfileTabIcon size={size} fill={color} />
      ),
    target: { name: 'Me', params: { screen: '_home' } },
  },
  ...(__DEV__ ? [playgroundItem] : []),
];

// Mobile drawer surfaces the destinations the bottom tab bar doesn't:
// the tab bar handles Dashboard/History/Inbox/Me, so the drawer carries
// the cross-cutting ones (Leads/Reservations/Plans) plus a Home shortcut and
// dev/native extras, and 设置/帮助 (pages of 我的). The AI edition's tab bar
// has neither order tab, so it lists both orders here.
const buildMobileDrawerItems = (
  aiEdition: boolean,
  canSeeRecords: boolean,
): SidebarItem[] => {
  const plansItem: SidebarItem = {
    key: 'Plans',
    label: '维保计划',
    renderIcon: (size, color, active) =>
      active ? (
        <PlansTabIconFill size={size} fill={color} />
      ) : (
        <PlansTabIcon size={size} fill={color} />
      ),
    target: { name: 'Plans' },
    feature: StoreFeature.MaintenancePlan,
  };
  const items: SidebarItem[] = [
    aiHomeItem,
    {
      key: 'Dashboard',
      label: aiEdition ? '在厂车辆' : '门店看板',
      renderIcon: iconmoon('home', 'home-outline'),
      target: { name: 'Dashboard' },
      feature: StoreFeature.OrderReception,
    },
    {
      key: 'History',
      label: '历史工单',
      renderIcon: (size, color, active) =>
        active ? (
          <RecordsTabIconFill size={size} fill={color} />
        ) : (
          <RecordsTabIcon size={size} fill={color} />
        ),
      target: { name: 'History' },
      feature: StoreFeature.OrderReception,
      // the workshop edition's bottom tab bar carries it; a restricted role
      // has no History tab at all
      hidden: !aiEdition || !canSeeRecords,
    },
    ...(aiEdition ? [plansItem] : []),
    {
      key: 'Leads',
      label: '商机管理',
      renderIcon: iconmoon('clipboard', 'clipboard'),
      target: { name: 'Leads' },
      feature: StoreFeature.Lead,
    },
    {
      key: 'Reservations',
      label: '预约管理',
      renderIcon: iconmoon('clock', 'clock'),
      target: { name: 'Reservations' },
      feature: StoreFeature.Reservation,
    },
    // the AI edition lists it with the orders
    ...(aiEdition ? [] : [plansItem]),
    {
      key: 'Inbox',
      label: '消息中心',
      renderIcon: (size, color, active) =>
        active ? (
          <MessageIconFill size={size} fill={color} />
        ) : (
          <MessageIcon size={size} fill={color} />
        ),
      target: { name: 'Inbox' },
      showUnread: true,
      // the bottom tab bar already carries it
      hidden: true,
    },
    {
      key: 'Me',
      label: '我的',
      renderIcon: iconmoon('user', 'user-o'),
      target: { name: 'Me' },
    },
    // Pages of the Me stack, over its home so back returns there; the AI
    // edition reaches them from 我的 only.
    {
      key: 'Settings',
      label: '设置',
      renderIcon: iconmoon('settings', 'settings-outline'),
      target: { name: 'Me', params: { screen: 'settings', initial: false } },
      hidden: aiEdition,
    },
    {
      key: 'Help',
      label: '帮助',
      renderIcon: iconmoon('help', 'help_outline'),
      target: { name: 'Me', params: { screen: 'help', initial: false } },
      hidden: aiEdition,
    },
  ];
  if (Platform.OS !== 'web') {
    items.push({
      key: 'Scan',
      label: '扫一扫',
      renderIcon: iconmoon('scan', 'scan'),
      target: { name: '_qrcodeScan' },
    });
    if (config.environment !== 'production') {
      items.push({
        key: 'WebExperience',
        label: 'Web View',
        renderIcon: iconmoon('global', 'global'),
        target: { name: '_embeddedWebExperience' },
      });
    }
  }
  if (__DEV__) {
    items.push(playgroundItem);
  }
  return items.filter(item => !item.hidden);
};

const kInactiveColor = 'rgba(255, 255, 255, 0.5)';
const kActiveColor = '#ffffff';

type AppSidebarProps = Partial<DrawerContentComponentProps> & {
  /** Desktop only: render the compact icon-rail variant. */
  collapsed?: boolean;
  /** Desktop only: toggle button next-state hint — controls the
   * chevron direction (collapsed→expand, expanded→collapse). */
  nextStateHint?: 'expand' | 'collapse';
  /** Desktop only: toggle button click handler. */
  onToggleCollapsed?: () => void;
  /** Desktop only: fires after a menu item navigation. The floating
   * variant uses this to dismiss itself once the user picks a target. */
  onItemNavigated?: () => void;
};

const findActiveTabName = (state: any): string => {
  // Walk the navigation state tree until we find the route whose name
  // matches a tab key. The drawer (when present) holds a single screen
  // 'Tabs' which itself holds the tab navigator; on desktop the sidebar
  // sits above the tab navigator directly. Either way the tab names
  // ('Dashboard', 'History', …) are the first match we hit.
  if (!state?.routes?.length) return 'Dashboard';
  const route = state.routes[state.index ?? 0];
  const knownTabs = new Set([
    'AiHome',
    'AiMarketing',
    'AiDashboards',
    'AiCoaching',
    'Dashboard',
    'History',
    'Plans',
    'Leads',
    'Reservations',
    'Me',
    'Inbox',
  ]);
  if (
    [
      'AiChat',
      'ApdmImport',
      'ApdmDashboard',
      'ApdmCampaign',
      'ApdmCampaignWizard',
      'ApdmCouponRedemption',
      'BiHome',
      'BiDashboards',
      'BiDashboard',
      'BiDashboardCard',
      'BiDatasets',
      'BiExports',
      'BiDataset',
      'PosterStudio',
    ].includes(route.name)
  )
    return 'AiHome';
  if (knownTabs.has(route.name)) return route.name;
  return findActiveTabName(route.state);
};

export const AppSidebar: FC<AppSidebarProps> = memo(props => {
  const drawerNav = props.navigation;
  const fallbackNav = useNavigation<any>();
  const navigation: any = drawerNav ?? fallbackNav;
  const isDesktop = useIsDesktopLayout();

  // Subscribe to the root navigation container so we see deeply-nested
  // tab/state changes (the sidebar is rendered above the tab navigator,
  // so the closest useNavigationState only sees stale snapshots of
  // child state).
  const [activeKey, setActiveKey] = useState<string>(() => {
    if (props.state) return findActiveTabName(props.state);
    const ref = getGlobalNavigationRef();
    return ref ? findActiveTabName(ref.getRootState()) : 'Dashboard';
  });
  useEffect(() => {
    if (props.state) {
      setActiveKey(findActiveTabName(props.state));
      return;
    }
    const ref = getGlobalNavigationRef();
    if (!ref) return;
    const update = () => setActiveKey(findActiveTabName(ref.getRootState()));
    update();
    return ref.addListener('state', update);
  }, [props.state]);

  const logout = useLogout();
  const { scanQrcode } = useQrcodeAuth({
    title: '扫一扫',
    onOtherCode: value => {
      if (!parseApdmCouponQr(value)) return false;
      navigation.navigate('ApdmCouponRedemption', { code: value });
      return true;
    },
  });
  const togglePresentationMode = useTogglePresentationMode();
  const { authService } = useServiceFactory();
  const [appCount, setAppCount] = useState<number | null>(null);
  useEffect(() => {
    authService
      .getApps()
      .then(apps => setAppCount(apps.length))
      .catch(() => setAppCount(null));
  }, [authService]);

  const closeIfMobile = useCallback(
    (fn: () => void, delay = 50) => {
      if (isDesktop || !drawerNav) {
        fn();
        return;
      }
      drawerNav.closeDrawer();
      delayed(fn, delay);
    },
    [drawerNav, isDesktop],
  );

  const onItemPress = useCallback(
    (item: SidebarItem) => {
      closeIfMobile(() => {
        if (item.key === 'Scan') {
          // scanQrcode owns the _qrcodeScan navigation: the screen is inert
          // without the onScan handler it injects.
          scanQrcode();
        } else {
          navigation.navigate(item.target.name as any, item.target.params);
        }
        props.onItemNavigated?.();
      });
    },
    [closeIfMobile, navigation, props, scanQrcode],
  );

  const onLogout = useCallback(() => {
    closeIfMobile(() => {
      logout().catch(onErrorIgnore);
    }, 250);
  }, [closeIfMobile, logout]);

  const onSwitchApp = useCallback(() => {
    closeIfMobile(() => {
      navigation.navigate('_appSwitch' as any);
    }, 250);
  }, [closeIfMobile, navigation]);

  const onSwitchMode = useCallback(() => {
    closeIfMobile(() => {
      void withConfirmation({
        title: '切换模式',
        message: '是否切换到演示模式? ',
      }).then(confirmed => {
        if (confirmed) {
          void togglePresentationMode();
        }
      });
    }, 250);
  }, [closeIfMobile, togglePresentationMode]);

  if (isDesktop) {
    return (
      <DesktopSidebar
        activeKey={activeKey}
        appCount={appCount}
        collapsed={!!props.collapsed}
        nextStateHint={
          props.nextStateHint ?? (props.collapsed ? 'expand' : 'collapse')
        }
        onToggleCollapsed={props.onToggleCollapsed}
        onItemPress={onItemPress}
        onLogout={onLogout}
        onSwitchApp={onSwitchApp}
        onSwitchMode={onSwitchMode}
      />
    );
  }

  return (
    <MobileDrawerMenu
      activeKey={activeKey}
      appCount={appCount}
      onClose={drawerNav?.closeDrawer}
      onItemPress={onItemPress}
      onLogout={onLogout}
      onSwitchApp={onSwitchApp}
      onSwitchMode={onSwitchMode}
    />
  );
});

type CommonProps = {
  activeKey: string;
  appCount: number | null;
  onItemPress: (item: SidebarItem) => void;
  onLogout: () => void;
  onSwitchApp: () => void;
  onSwitchMode: () => void;
};

const kDesktopBg = '#0f172a';

const DesktopSidebar: FC<
  CommonProps & {
    collapsed: boolean;
    nextStateHint: 'expand' | 'collapse';
    onToggleCollapsed?: () => void;
  }
> = memo(
  ({
    activeKey,
    appCount,
    collapsed,
    nextStateHint,
    onItemPress,
    onLogout,
    onSwitchApp,
    onSwitchMode,
    onToggleCollapsed,
  }) => {
    const { rpx } = useResponsiveContext();
    const aiEdition = useCurrentUser()?.store.editionProductLine === 'ai';
    const canSeeRecords = useCanSeeServiceRecords();
    const tabItems = useEntitledSidebarItems(
      useMemo(
        () =>
          aiEdition ? buildAiEditionSidebarItems(canSeeRecords) : TAB_ITEMS,
        [aiEdition, canSeeRecords],
      ),
    );
    return (
      <View
        style={{ flex: 1, backgroundColor: kDesktopBg }}
        css={`
          padding-top: ${rpx(12)}px;
        `}
      >
        <ToggleButton
          collapsed={collapsed}
          nextStateHint={nextStateHint}
          onPress={onToggleCollapsed}
        />

        {collapsed ? (
          <CollapsedAvatar />
        ) : (
          <>
            <UserInfoView />
            <Divider mt={rpx(8)} mb={rpx(4)} />
          </>
        )}

        <View
          css={`
            padding: ${rpx(8)}px 0;
          `}
        >
          {tabItems.map(item =>
            collapsed ? (
              <CollapsedItemRow
                key={item.key}
                item={item}
                active={activeKey === item.key}
                onPress={onItemPress}
              />
            ) : (
              <SidebarItemRow
                key={item.key}
                item={item}
                active={activeKey === item.key}
                onPress={onItemPress}
              />
            ),
          )}
        </View>

        <Divider mt={rpx(4)} mb={rpx(4)} />

        <View
          css={`
            padding: ${rpx(8)}px 0;
          `}
        >
          {SECONDARY_ITEMS.map(item =>
            collapsed ? (
              <CollapsedItemRow
                key={item.key}
                item={item}
                active={activeKey === item.key}
                onPress={onItemPress}
              />
            ) : (
              <SidebarItemRow
                key={item.key}
                item={item}
                active={activeKey === item.key}
                onPress={onItemPress}
              />
            ),
          )}
        </View>

        <View
          css={`
            flex: 1;
          `}
        />

        {collapsed ? (
          <CollapsedFooter
            appCount={appCount}
            onLogout={onLogout}
            onSwitchApp={onSwitchApp}
          />
        ) : (
          <>
            <Footer
              appCount={appCount}
              onLogout={onLogout}
              onSwitchApp={onSwitchApp}
              onSwitchMode={onSwitchMode}
            />
            <VersionInfo />
          </>
        )}
      </View>
    );
  },
);

const ToggleButton: FC<{
  collapsed: boolean;
  nextStateHint: 'expand' | 'collapse';
  onPress?: () => void;
}> = memo(({ collapsed, nextStateHint, onPress }) => {
  const { rpx } = useResponsiveContext();
  if (!onPress) return null;
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        alignSelf: collapsed ? 'center' : 'flex-end',
        marginRight: collapsed ? 0 : rpx(12),
        marginTop: rpx(4),
        marginBottom: rpx(4),
        width: rpx(28),
        height: rpx(28),
        borderRadius: rpx(8),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: pressed ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
      })}
    >
      <Icon
        name={nextStateHint === 'expand' ? 'chevron-right' : 'chevron-left'}
        size={rpx(16)}
        color={kInactiveColor}
      />
    </Pressable>
  );
});

const CollapsedAvatar = memo(() => {
  const { rpx } = useResponsiveContext();
  const profile = useCurrentUser();
  const size = rpx(36);
  return (
    <View
      style={{
        alignSelf: 'center',
        width: size,
        height: size,
        borderRadius: size / 2,
        marginVertical: rpx(12),
        overflow: 'hidden',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      }}
    >
      <Avatar
        uri={profile?.user?.avatar}
        name={
          profile?.user?.name ??
          profile?.user?.nick ??
          profile?.user?.userName ??
          'U'
        }
        textStyle={{
          fontFamily: FontFamily.NotoSans.Bold,
          fontSize: rpx(14),
          lineHeight: rpx(20),
        }}
        css={`
          width: ${size}px;
          height: ${size}px;
          border-radius: ${size / 2}px;
        `}
      />
    </View>
  );
});

const CollapsedItemRow: FC<{
  item: SidebarItem;
  active: boolean;
  onPress: (item: SidebarItem) => void;
}> = memo(({ item, active, onPress }) => {
  const { rpx } = useResponsiveContext();
  const handlePress = useCallback(() => onPress(item), [item, onPress]);
  return (
    <Pressable
      onPress={handlePress}
      // @ts-ignore react-native-web supports `title` for native tooltips
      title={item.label}
      style={({ pressed }) => [
        {
          alignSelf: 'center',
          width: rpx(40),
          height: rpx(40),
          marginVertical: rpx(4),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: active
            ? 'rgba(99, 130, 234, 0.18)'
            : pressed
            ? 'rgba(255, 255, 255, 0.06)'
            : 'transparent',
        },
      ]}
    >
      {item.renderIcon(rpx(20), active ? '#93c5fd' : kInactiveColor, active)}
      {item.showUnread && (
        <View
          style={{
            position: 'absolute',
            top: rpx(4),
            right: rpx(4),
          }}
        >
          <InboxBadge />
        </View>
      )}
    </Pressable>
  );
});

const CollapsedFooter: FC<{
  appCount: number | null;
  onLogout: () => void;
  onSwitchApp: () => void;
}> = memo(({ appCount, onLogout, onSwitchApp }) => {
  const { rpx } = useResponsiveContext();
  const hasMultipleApps = appCount != null && appCount > 1;
  return (
    <View style={{ alignItems: 'center', paddingBottom: rpx(12) }}>
      {hasMultipleApps && (
        <Pressable
          onPress={onSwitchApp}
          // @ts-ignore react-native-web
          title="切换门店"
          style={({ pressed }) => ({
            width: rpx(40),
            height: rpx(40),
            borderRadius: rpx(10),
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: rpx(4),
            backgroundColor: pressed
              ? 'rgba(255, 255, 255, 0.06)'
              : 'transparent',
          })}
        >
          <SwitchIcon size={rpx(18)} color={kInactiveColor} />
        </Pressable>
      )}
      <Pressable
        onPress={onLogout}
        // @ts-ignore react-native-web
        title="退出登录"
        style={({ pressed }) => ({
          width: rpx(40),
          height: rpx(40),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: pressed
            ? 'rgba(255, 255, 255, 0.06)'
            : 'transparent',
        })}
      >
        <LogoutIcon size={rpx(18)} color={kInactiveColor} />
      </Pressable>
    </View>
  );
});

const MobileDrawerMenu: FC<
  CommonProps & {
    onClose?: () => void;
  }
> = memo(
  ({
    activeKey,
    appCount,
    onClose,
    onItemPress,
    onLogout,
    onSwitchApp,
    onSwitchMode,
  }) => {
    const { rpx } = useResponsiveContext();
    const aiEdition = useCurrentUser()?.store.editionProductLine === 'ai';
    const canSeeRecords = useCanSeeServiceRecords();
    const items = useEntitledSidebarItems(
      useMemo(
        () => buildMobileDrawerItems(aiEdition, canSeeRecords),
        [aiEdition, canSeeRecords],
      ),
    );
    return (
      <LinearGradient
        colors={['#009FD4', '#2669db']}
        css={`
          flex: 1;
        `}
      >
        <View
          css={`
            flex: 1;
          `}
        >
          <TouchableOpacity
            onPress={onClose}
            hitSlop={PredefinedHitSlops.large}
            css={`
              margin: ${rpx(15)}px ${rpx(16)}px ${rpx(5)}px ${rpx(16)}px;
              align-self: flex-start;
            `}
          >
            <Icon name="close" size={rpx(28)} color="white" />
          </TouchableOpacity>

          <UserInfoView />

          <View
            css={`
              flex: 1;
              margin-top: ${rpx(8)}px;
            `}
          >
            {items.map(item => (
              <MobileDrawerItemRow
                key={item.key}
                item={item}
                active={
                  activeKey === item.key &&
                  item.key !== 'Scan' &&
                  item.key !== 'WebExperience' &&
                  item.key !== 'Playground'
                }
                onPress={onItemPress}
              />
            ))}
          </View>

          <Footer
            appCount={appCount}
            onLogout={onLogout}
            onSwitchApp={onSwitchApp}
            onSwitchMode={onSwitchMode}
          />

          <VersionInfo />
        </View>
      </LinearGradient>
    );
  },
);

const Footer: FC<{
  appCount: number | null;
  onLogout: () => void;
  onSwitchApp: () => void;
  onSwitchMode: () => void;
}> = memo(({ appCount, onLogout, onSwitchApp, onSwitchMode }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        padding: 0 ${rpx(21)}px;
        flex-direction: column-reverse;
      `}
    >
      <StackLayout
        direction="row"
        alignItems="center"
        gap={rpx(12)}
        css={`
          margin: ${rpx(8)}px 0;
        `}
      >
        <LogoutButton onPress={onLogout} />
        {appCount != null && appCount > 1 && (
          <SwitchAppButton onPress={onSwitchApp} />
        )}
      </StackLayout>
      {!isMobileBrowser() && (
        <SwitchMode
          onPress={onSwitchMode}
          css={`
            margin: ${rpx(8)}px 0;
          `}
        />
      )}
    </View>
  );
});

const Divider: FC<{ mt?: number; mb?: number }> = memo(({ mt, mb }) => (
  <View
    style={{
      marginTop: mt,
      marginBottom: mb,
      marginHorizontal: 16,
      height: StyleSheet.hairlineWidth,
      backgroundColor: 'rgba(255, 255, 255, 0.18)',
    }}
  />
));

const SidebarItemRow: FC<{
  item: SidebarItem;
  active: boolean;
  onPress: (item: SidebarItem) => void;
}> = memo(({ item, active, onPress }) => {
  const { rpx } = useResponsiveContext();
  const handlePress = useCallback(() => onPress(item), [item, onPress]);
  return (
    <Pressable
      onPress={handlePress}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          marginHorizontal: rpx(12),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(10),
          borderRadius: rpx(8),
          backgroundColor: active
            ? 'rgba(99, 130, 234, 0.18)'
            : pressed
            ? 'rgba(255, 255, 255, 0.06)'
            : 'transparent',
        },
      ]}
    >
      {item.renderIcon(rpx(20), active ? '#93c5fd' : kInactiveColor, active)}
      <View style={{ marginLeft: rpx(14), flexShrink: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            numberOfLines={1}
            style={{
              color: active ? kActiveColor : kInactiveColor,
              fontFamily: active
                ? FontFamily.NotoSans.Medium
                : FontFamily.NotoSans.Light,
              fontSize: rpx(15),
              opacity: 1,
              flexShrink: 0,
            }}
          >
            {item.label}
          </Text>
          <SidebarItemBadge item={item} />
        </View>
        {item.aiTrial ? <SidebarCreditBalance color={kInactiveColor} /> : null}
      </View>
      <View style={{ flex: 1 }} />
      {item.showUnread && <InboxBadge />}
    </Pressable>
  );
});

// Old drawer item styling: larger label, more left padding, no row background
// highlight (active state only swaps icon/label opacity).
const MobileDrawerItemRow: FC<{
  item: SidebarItem;
  active: boolean;
  onPress: (item: SidebarItem) => void;
}> = memo(({ item, active, onPress }) => {
  const { rpx } = useResponsiveContext();
  const handlePress = useCallback(() => onPress(item), [item, onPress]);
  return (
    <Pressable
      onPress={handlePress}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          marginHorizontal: rpx(16),
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(12),
          opacity: pressed ? 0.7 : 1,
        },
      ]}
    >
      {item.renderIcon(rpx(20), active ? kActiveColor : kInactiveColor, active)}
      <View style={{ marginLeft: rpx(16), flexShrink: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            numberOfLines={1}
            style={{
              color: '#fff',
              opacity: active ? 1 : 0.5,
              fontFamily: FontFamily.NotoSans.Light,
              fontSize: rpx(18),
              flexShrink: 0,
            }}
          >
            {item.label}
          </Text>
          <SidebarItemBadge item={item} />
        </View>
        {item.aiTrial ? (
          <SidebarCreditBalance color="rgba(255, 255, 255, 0.8)" />
        ) : null}
      </View>
      <View style={{ flex: 1 }} />
      {item.showUnread && <InboxBadge />}
    </Pressable>
  );
});

const InboxBadge = memo(() => {
  const feedCount = useFeedUnreadCount();
  const chatCount = useChatUnreadCount();
  const count = feedCount + chatCount;
  if (count <= 0) return null;
  return <UnreadBadge count={count} />;
});
